// ---------------------------------------------------------------------------
// Resources Tab — data model
// ---------------------------------------------------------------------------

export type ResourceType = 'Employer' | 'Community' | 'Other';

export type ResourceCategory =
  | 'housing'
  | 'debt'
  | 'transportation'
  | 'legal'
  | 'tax'
  | 'food'
  | 'cash_support'
  | 'healthcare'
  | 'childcare'
  | 'education'
  | 'other';

export type EligibilityStatus =
  | 'Eligible'
  | 'Potentially Eligible'
  | 'Not Eligible'
  | 'Needs More Info';

export type ConfidenceLevel = 'high' | 'medium' | 'low';

export type ContactMethod = 'phone' | 'email' | 'web';

export type ResourceSortKey = 'best_match' | 'highest_confidence' | 'most_recent' | 'most_used';

// ---------------------------------------------------------------------------
// Resource (provider-level record)
// ---------------------------------------------------------------------------

export interface Resource {
  resourceId: string;
  name: string;
  type: ResourceType;
  category: ResourceCategory;
  providerName: string;
  coverageRegion: string;
  contactPhone?: string;
  contactEmail?: string;
  websiteUrl?: string;
  tags: string[];
  description: string;
  sourceSystem: string;
  lastVerifiedAt: string;
}

// ---------------------------------------------------------------------------
// Eligibility (per client-resource pair)
// ---------------------------------------------------------------------------

export interface EligibilityRecord {
  eligibilityStatus: EligibilityStatus;
  eligibilityScore: number; // 0–100
  criteriaMet: string[];
  criteriaMissing: string[];
  criteriaUnknown: string[];
  whyShownText: string;
  whyNotShownText?: string;
  confidenceLevel: ConfidenceLevel;
  evidenceSources: string[];
}

// ---------------------------------------------------------------------------
// Ranked resource (resource + eligibility + rank)
// ---------------------------------------------------------------------------

export interface RankedResource extends Resource, EligibilityRecord {
  rankScore: number; // 0–100
  rankReasonCodes: string[];
  // Action tracking
  savedByFA: boolean;
  contactedAt?: string;
  contactMethod?: ContactMethod;
  outcomeLinked?: string;
}

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

export interface ResourceFilters {
  search: string;
  type: ResourceType | 'All';
  category: ResourceCategory | 'All';
  eligibilityStatus: EligibilityStatus | 'All';
  sortKey: ResourceSortKey;
}

export const DEFAULT_RESOURCE_FILTERS: ResourceFilters = {
  search: '',
  type: 'All',
  category: 'All',
  eligibilityStatus: 'All',
  sortKey: 'best_match',
};

// ---------------------------------------------------------------------------
// Client context snapshot (used for matching display)
// ---------------------------------------------------------------------------

export interface ClientContextSnapshot {
  employer: string;
  region: string;
  tenure: string;
  employmentType: string;
  householdSize: number;
  knownNeeds: string[];
  lastUpdatedAt: string;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface ResourcesState {
  context: ClientContextSnapshot;
  allResources: RankedResource[];
  filters: ResourceFilters;
  /** Explorer pagination */
  explorerPage: number;
  readonly explorerPageSize: number;
}

// ---------------------------------------------------------------------------
// Static catalogues
// ---------------------------------------------------------------------------

export const RESOURCE_CATEGORIES: { value: ResourceCategory | 'All'; label: string }[] = [
  { value: 'All',           label: 'All Categories' },
  { value: 'housing',       label: 'Housing' },
  { value: 'debt',          label: 'Debt' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'legal',         label: 'Legal' },
  { value: 'tax',           label: 'Tax' },
  { value: 'food',          label: 'Food' },
  { value: 'cash_support',  label: 'Cash Support' },
  { value: 'healthcare',    label: 'Healthcare' },
  { value: 'childcare',     label: 'Childcare' },
  { value: 'education',     label: 'Education' },
  { value: 'other',         label: 'Other' },
];

export const RESOURCE_TYPES: { value: ResourceType | 'All'; label: string }[] = [
  { value: 'All',       label: 'All Types' },
  { value: 'Employer',  label: 'Employer' },
  { value: 'Community', label: 'Community' },
  { value: 'Other',     label: 'Other' },
];

export const ELIGIBILITY_STATUSES: { value: EligibilityStatus | 'All'; label: string }[] = [
  { value: 'All',                  label: 'All Statuses' },
  { value: 'Eligible',             label: 'Eligible' },
  { value: 'Potentially Eligible', label: 'Potentially Eligible' },
  { value: 'Needs More Info',      label: 'Needs More Info' },
  { value: 'Not Eligible',         label: 'Not Eligible' },
];

export const SORT_OPTIONS: { value: ResourceSortKey; label: string }[] = [
  { value: 'best_match',        label: 'Best Match' },
  { value: 'highest_confidence', label: 'Highest Confidence' },
  { value: 'most_recent',       label: 'Most Recently Verified' },
  { value: 'most_used',         label: 'Most Used' },
];

// ---------------------------------------------------------------------------
// Derived helpers
// ---------------------------------------------------------------------------

export function eligibilityStatusSeverity(
  status: EligibilityStatus
): 'success' | 'warn' | 'danger' | 'secondary' {
  switch (status) {
    case 'Eligible':             return 'success';
    case 'Potentially Eligible': return 'warn';
    case 'Needs More Info':      return 'warn';
    case 'Not Eligible':         return 'danger';
  }
}

export function confidenceLevelLabel(level: ConfidenceLevel): string {
  switch (level) {
    case 'high':   return 'High';
    case 'medium': return 'Medium';
    case 'low':    return 'Low';
  }
}

export function categoryLabel(cat: ResourceCategory): string {
  const found = RESOURCE_CATEGORIES.find(c => c.value === cat);
  return found?.label ?? cat;
}

export function applyFilters(
  resources: RankedResource[],
  filters: ResourceFilters
): RankedResource[] {
  let result = resources;

  if (filters.type !== 'All') {
    result = result.filter(r => r.type === filters.type);
  }
  if (filters.category !== 'All') {
    result = result.filter(r => r.category === filters.category);
  }
  if (filters.eligibilityStatus !== 'All') {
    result = result.filter(r => r.eligibilityStatus === filters.eligibilityStatus);
  }
  if (filters.search.trim()) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      r =>
        r.name.toLowerCase().includes(q) ||
        r.providerName.toLowerCase().includes(q) ||
        r.category.toLowerCase().includes(q) ||
        r.tags.some(t => t.toLowerCase().includes(q))
    );
  }

  return sortResources(result, filters.sortKey);
}

export function sortResources(
  resources: RankedResource[],
  key: ResourceSortKey
): RankedResource[] {
  const copy = [...resources];
  switch (key) {
    case 'best_match':
      return copy.sort((a, b) => b.rankScore - a.rankScore);
    case 'highest_confidence':
      return copy.sort((a, b) => b.eligibilityScore - a.eligibilityScore);
    case 'most_recent':
      return copy.sort((a, b) => b.lastVerifiedAt.localeCompare(a.lastVerifiedAt));
    case 'most_used':
      // Stable secondary sort on rankScore when usage data is unavailable
      return copy.sort((a, b) => b.rankScore - a.rankScore);
  }
}
